import React, { useEffect, useState } from 'react';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { DateTime } from 'luxon';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { Maybe, ScanSessionAdminRow, ScanSessionChangeHistory, ScanSessionJSON, VisitReason } from '@/common/types';
import {
    getAllKeyValuePairs,
    getAssessmentNameDashID,
    preventParentLinkClick,
    resultValueWithUnitsOrEmpty,
} from '@/common/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { Button } from '@/components/common/button/button';
import { useAssessmentsContext } from '@/components/context/hooks/AssessmentsContext';
import { useModalContext } from '@/components/context/hooks/ModalContext';
import { resultIsError } from '@/services/HttpService';
import { ScanSessionDetailedDetails, ScanSessionDetailsRow, ScanSessionResultsRow } from './styles';

const formatDateTime = (iso: string) => {
    const dateTime = DateTime.fromISO(iso);
    return `${dateTime.toLocaleString(DateTime.DATE_SHORT)} ${dateTime.toLocaleString(DateTime.TIME_WITH_SECONDS)}`;
};

interface ScanSessionDetailsAccordionProps {
    title: string;
    defaultExpanded?: boolean;
    children: React.ReactNode;
}
const ScanSessionDetailsAccordion = ({ title, defaultExpanded = true, children }: ScanSessionDetailsAccordionProps) => {
    return (
        <Accordion defaultExpanded={defaultExpanded}>
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ fontSize: '1.5rem' }} />}>
                <strong>{title}: </strong>
            </AccordionSummary>
            <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
    );
};

interface MetaRowsProps {
    meta: Partial<ScanSessionJSON['meta']>;
    nullValue?: React.ReactNode;
}
const MetaRows = ({ meta, nullValue }: MetaRowsProps) => {
    const { visitReasons, ...rest } = meta;
    const primitiveArrays: [string, string][] = [];
    const nested: Record<string, unknown> = {};
    Object.entries(rest).forEach(([key, value]) => {
        if (Array.isArray(value) && value.every((v) => v === null || typeof v !== 'object')) {
            primitiveArrays.push([key, value.join(', ')]);
        } else {
            nested[key] = value;
        }
    });
    const pairs = [...getAllKeyValuePairs(nested), ...primitiveArrays];
    if (visitReasons !== undefined) {
        pairs.push([
            'visitReasons',
            Array.isArray(visitReasons)
                ? (visitReasons as VisitReason[]).map((vr) => vr.reason).join(', ')
                : visitReasons,
        ]);
    }

    return (
        <>
            {pairs
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map(([key, value]) => {
                    return (
                        <ScanSessionResultsRow key={key}>
                            {key}: {value === null && nullValue ? nullValue : <strong>{value}</strong>}
                        </ScanSessionResultsRow>
                    );
                })}
        </>
    );
};

const ScanSessionChangeHistoryAccordion = ({ sessionUUID }: { sessionUUID: string }) => {
    const { httpService } = useHttpContext();
    const [history, setHistory] = useState<Maybe<ScanSessionChangeHistory[]>>(undefined);
    const [error, setError] = useState<Maybe<string>>(undefined);

    useEffect(() => {
        (async () => {
            const res = await httpService.adminGetScanSessionChangeHistory(sessionUUID);
            if (!res || resultIsError(res)) {
                setError(res?.message || 'Could not load change history.');
                return;
            }
            setHistory(res);
        })();
    }, [httpService, sessionUUID]);

    const renderContent = () => {
        if (error) {
            return error;
        }
        if (!history) {
            return <LoadingSpinner />;
        }
        if (history.length === 0) {
            return 'No changes.';
        }
        return history.map((entry, index) => {
            const isInitial = index === history.length - 1;
            const nullValue = <em>{isInitial ? 'not set' : 'removed'}</em>;
            return (
                <ScanSessionDetailsRow key={entry.historyCreated}>
                    <strong>
                        {formatDateTime(entry.historyCreated)}
                        {isInitial && ' (Initial values)'}
                    </strong>
                    {entry.participant !== undefined && (
                        <ScanSessionResultsRow>
                            participant: {entry.participant ? <strong>{entry.participant.uid}</strong> : nullValue}
                        </ScanSessionResultsRow>
                    )}
                    {entry.meta && <MetaRows meta={entry.meta} nullValue={nullValue} />}
                </ScanSessionDetailsRow>
            );
        });
    };

    return (
        <ScanSessionDetailsAccordion title="Change History" defaultExpanded={false}>
            {renderContent()}
        </ScanSessionDetailsAccordion>
    );
};

interface ScanDetailsActionProps {
    row: ScanSessionAdminRow;
}
export const ScanDetailsAction = (props: ScanDetailsActionProps) => {
    const { row } = props;
    const { setDialog, setDialogOpen } = useModalContext();
    const { assessmentsDict } = useAssessmentsContext();

    const handleClick = (e: React.MouseEvent) => {
        preventParentLinkClick(e);
        setDialog(
            'Session Details',
            <>
                <ScanSessionDetailsRow>
                    <strong>Organization: </strong>
                    {row.organizationName}
                </ScanSessionDetailsRow>
                <ScanSessionDetailsRow>
                    <strong>Location: </strong>
                    {row.locationName}
                </ScanSessionDetailsRow>
                {row.session?.data.assessmentId && (
                    <ScanSessionDetailsRow>
                        <strong>Assessment: </strong>
                        {getAssessmentNameDashID(row.session.data.assessmentId, assessmentsDict)}
                    </ScanSessionDetailsRow>
                )}
                <ScanSessionDetailsRow>
                    <strong>Provider Name: </strong>
                    {row.practitionerName}
                </ScanSessionDetailsRow>
                <ScanSessionDetailsRow>
                    <strong>Session UUID: </strong>
                    {row.sessionUUID}
                </ScanSessionDetailsRow>
                <ScanSessionDetailsRow>
                    <strong>Created: </strong>
                    {formatDateTime(row.createdDate)}
                </ScanSessionDetailsRow>
                <ScanSessionDetailsRow>
                    <strong>Updated: </strong>
                    {formatDateTime(row.updatedDate)}
                </ScanSessionDetailsRow>
                {row.session && (
                    <ScanSessionDetailsRow>
                        <strong>Detailed Details: </strong>
                        <ScanSessionDetailedDetails>
                            {row.session.data.results && (
                                <ScanSessionDetailsAccordion title="Results">
                                    {row.session.data.results.map((result) => {
                                        return (
                                            <ScanSessionResultsRow key={result.id}>
                                                {result.name}: <strong>{resultValueWithUnitsOrEmpty(result)}</strong>
                                            </ScanSessionResultsRow>
                                        );
                                    })}
                                </ScanSessionDetailsAccordion>
                            )}
                            {row.session.meta && (
                                <ScanSessionDetailsAccordion title="Meta">
                                    <MetaRows meta={row.session.meta} />
                                </ScanSessionDetailsAccordion>
                            )}
                        </ScanSessionDetailedDetails>
                    </ScanSessionDetailsRow>
                )}
                <ScanSessionChangeHistoryAccordion sessionUUID={row.sessionUUID} />
            </>,
        );
        setDialogOpen(true);
    };

    return (
        <Button variant="secondary" onClick={handleClick} size="small">
            Details
        </Button>
    );
};
